import { useEffect, useMemo, useState } from 'react';
import { useNarrativesCommunityInfinite } from '../hooks/useNarrativesCommunityInfinite';
import { NarrativeCardPopulated } from '@/components/NarrativeCard';
import {
  NARRATIVE_COMMUNITY_CURSOR_QUERY_PARAM,
  NARRATIVE_COMMUNITY_LIST_PAGE_SIZE,
} from '../utils';
import { Button } from '@simplywallst/ui-core';
import { useQueryStringParam } from '@/hooks/useQueryStringParam';
import EyeSolid from '@simplywallst/ui-core/icons/EyeSolid';
import { NarrativesEmptyPanel } from '../../NarrativesEmptyPanel';

export const NarrativeCommunityListMain = () => {
  const { setItem, removeItem, getItem } = useQueryStringParam();
  const initialStartCursor = getItem(NARRATIVE_COMMUNITY_CURSOR_QUERY_PARAM);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const {
    data: narratives,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useNarrativesCommunityInfinite({
    first: NARRATIVE_COMMUNITY_LIST_PAGE_SIZE,
    last: NARRATIVE_COMMUNITY_LIST_PAGE_SIZE,
    after: initialStartCursor ?? undefined,
  });
  const allNarratives = useMemo(
    () =>
      narratives?.pages.flatMap((page) =>
        page.narratives.edges.map((edge) => edge.node)
      ) ?? [],
    [narratives]
  );
  const firstPage = narratives?.pages[0];
  const secondToLastPage = narratives?.pages[narratives.pages.length - 2];

  /**
   * Cases to handle
   * 1. There are more pages to load and I have loaded already some pages (cursor must point to the last loaded page first edge)
   * 2. There are more pages to load and I have not loaded any page (cursor must be null, begin from the start)
   * 3. There are no more pages to load and I have loaded some pages (cursor must point to the last loaded page last edge)
   */
  const forwardCursor = secondToLastPage?.narratives.pageInfo.endCursor;

  /**
   * Cases to handle
   * 1. There are more prior pages to load and I have loaded already some pages (cursor must point to the first loaded page first edge)
   * 2. There are no more prior pages to load and I have loaded some pages (cursor must be null, begin from the start)
   * 3. There are no more prior pages to load and I have not loaded any page (cursor must be null)
   */
  const backwardCursor = firstPage.narratives.pageInfo.hasPreviousPage
    ? firstPage.narratives.pageInfo.startCursor
    : null;
  const startCursor = direction === 'forward' ? forwardCursor : backwardCursor;

  useEffect(() => {
    if (!startCursor) {
      removeItem(NARRATIVE_COMMUNITY_CURSOR_QUERY_PARAM);
      return;
    }

    setItem(NARRATIVE_COMMUNITY_CURSOR_QUERY_PARAM, startCursor);
  }, [startCursor, removeItem, setItem]);
  const isLoading = isFetchingNextPage || isFetchingPreviousPage;

  if (!allNarratives.length)
    return (
      <NarrativesEmptyPanel>
        There are no community narratives available.
        {/* TODO Add create narrative CTA button */}
      </NarrativesEmptyPanel>
    );

  return (
    <div className="grid gap-x2">
      {hasPreviousPage && (
        <div className="grid grid-cols-[minmax(0,300px)] place-content-center">
          <Button
            styleType="loudLarge"
            isLoading={isFetchingPreviousPage}
            disabled={isLoading}
            onClick={() => {
              setDirection('backward');
              fetchPreviousPage();
            }}
            alignIcon="left"
          >
            <EyeSolid />
            Load previous page
          </Button>
        </div>
      )}
      <section
        className="grid grid-cols-1 md:grid-cols-2 lg: lg:grid-cols-3 gap-x2"
        role="list"
      >
        {allNarratives.map((narrative) => (
          <div key={narrative.id} role="listitem">
            <NarrativeCardPopulated showCompanyIcon narrative={narrative} />
          </div>
        ))}
      </section>
      {hasNextPage && (
        <div className="grid grid-cols-[minmax(0,300px)] place-content-center">
          <Button
            styleType="loudLarge"
            isLoading={isFetchingNextPage}
            disabled={isLoading}
            onClick={() => {
              setDirection('forward');
              fetchNextPage();
            }}
            alignIcon="left"
          >
            <EyeSolid />
            Load next page
          </Button>
        </div>
      )}
    </div>
  );
};
